import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../LanguageContext';
import { peekRateLimitReturnPath } from '../utils/installFetchRateLimitRedirect';

function readSecondsRemaining() {
  if (typeof window === 'undefined') return 0;
  try {
    const sec = parseInt(sessionStorage.getItem('shefaRateLimitRetryAfterSec'), 10);
    const at = parseInt(sessionStorage.getItem('shefaRateLimitStartedAtMs'), 10);
    if (!Number.isFinite(sec) || !Number.isFinite(at)) return 0;
    const end = at + sec * 1000;
    return Math.max(0, Math.ceil((end - Date.now()) / 1000));
  } catch {
    return 0;
  }
}

function clearRateLimitSessionHints() {
  try {
    sessionStorage.removeItem('shefaRateLimitRetryAfterSec');
    sessionStorage.removeItem('shefaRateLimitStartedAtMs');
    sessionStorage.removeItem('shefaRateLimitReturnTo');
  } catch {
    /* ignore */
  }
}

export default function RateLimitedPage() {
  const { t } = useLang();
  const [secondsLeft, setSecondsLeft] = useState(() => readSecondsRemaining());

  useEffect(() => {
    const tick = () => setSecondsLeft(readSecondsRemaining());
    tick();
    const id = window.setInterval(tick, 500);
    return () => window.clearInterval(id);
  }, []);

  const resumeTarget = useMemo(() => peekRateLimitReturnPath(), []);

  const tryResume = useCallback(() => {
    clearRateLimitSessionHints();
    window.location.assign(resumeTarget);
  }, [resumeTarget]);

  const retryDisabled = secondsLeft > 0;

  return (
    <main className="container py-4 py-md-5" style={{ maxWidth: 560 }}>
      <div
        className="p-4 p-md-5 rounded-4 border"
        style={{
          background: 'var(--shefa-glass-fill)',
          borderColor: 'var(--shefa-glass-border)',
          boxShadow: 'var(--shefa-card-shadow)',
        }}
      >
        <div className="d-flex align-items-start gap-3 mb-3">
          <span
            className="d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
            style={{
              width: 48,
              height: 48,
              background: 'color-mix(in srgb, var(--shefa-brand-green) 22%, transparent)',
              color: 'var(--shefa-brand-green)',
            }}
            aria-hidden
          >
            <i className="fas fa-leaf fa-lg" />
          </span>
          <div className="flex-grow-1 min-w-0">
            <p className="text-uppercase small text-secondary mb-1">{t('rateLimitPage.eyebrow')}</p>
            <h1 className="h4 mb-0" style={{ color: 'var(--shefa-text-primary)' }}>
              {t('rateLimitPage.title')}
            </h1>
          </div>
        </div>
        <p className="mb-3" style={{ color: 'var(--shefa-text-secondary)', lineHeight: 1.6 }}>
          {t('rateLimitPage.body')}
        </p>
        <p className="mb-2" style={{ color: 'var(--shefa-text-secondary)', lineHeight: 1.6 }}>
          {t('rateLimitPage.hint')}
        </p>
        <p className="mb-3 small" style={{ color: 'var(--shefa-text-secondary)', lineHeight: 1.55 }}>
          {t('rateLimitPage.tabsHint')}
        </p>
        <p
          className="mb-4 fw-medium"
          style={{ color: 'var(--shefa-text-primary)', lineHeight: 1.5 }}
          aria-live="polite"
        >
          {t('rateLimitPage.retryIn')(secondsLeft)}
        </p>
        <div className="d-flex flex-column flex-sm-row gap-2">
          <button
            type="button"
            className="btn btn-success flex-sm-fill"
            onClick={tryResume}
            disabled={retryDisabled}
            title={retryDisabled ? t('rateLimitPage.retryIn')(secondsLeft) : undefined}
          >
            {t('rateLimitPage.ctaRetry')}
          </button>
          <Link className="btn btn-outline-secondary flex-sm-fill" to="/" onClick={clearRateLimitSessionHints}>
            {t('rateLimitPage.ctaHome')}
          </Link>
        </div>
      </div>
    </main>
  );
}
